import type { IssueRevisionSeverity } from '../issuesV2';
import type { PackageLabelAssignmentWithName } from '../model-typings/packagelabelassignment';
import type { PaginatedRequestQuery, PaginatedResponseWithTotal } from '../types/routes/pagination';

/**
 * The available status values when comparing snippets.
 */
export const SnippetStatusValues = ['new', 'removed', 'unchanged'] as const;

/**
 * Represents the type of available statuses when comparing snippets.
 */
export type SnippetStatus = typeof SnippetStatusValues[number];

/**
 * Snippets can be detected in a few different ways:
 * 1. Snippet is a partial match to a third party file.
 * 2. File, according to ScanOSS, is a 100% match to a third party file.
 */
export type SnippetKind = 'snippet' | 'file';

export type SnippetLicense = {
  signature: string;
  type: 'declared' | 'discovered';
  status?: 'denied' | 'flagged' | 'unknown';
  issueId?: number;
};

export type SnippetLicensingIssueCounts = {
  denied: number;
  flagged: number;
  unknown: number;
};

export type SnippetSecurityIssueCounts = IssueRevisionSeverity;

export type SnippetIssueCounts = {
  licensing: SnippetLicensingIssueCounts;
  security: SnippetSecurityIssueCounts;
};

export type SnippetRejectionDetails = {
  rejectedAt: string;
  rejectedBy?: string;
};

/**
 * Represents an individual file wherein a code snippet was detected.
 */
export type SnippetMatch = {
  /**
   * The file path where the snippet was detected.
   */
  path: string;
  /**
   * Match percentage as a floating point number between 0 and 1.
   */
  matchPercentage: number;
  /**
   * Includes metadata about whether this individual match's rejection status.
   */
  rejectionDetails?: SnippetRejectionDetails;
};

/**
 * This type represents a minimal (in the core sense) version of a snippet that
 * we plan on getting from Sparkle. Notably, it lacks certain core-specific information
 * such as licenses and issue counts. We will have to hydrate that information post-fetch.
 */
export type MinimalSnippet = {
  id: string;
  packageId: string;
  purl: string;
  locator?: string;
  package: string;
  version: string;
  kind: SnippetKind;
  matches: SnippetMatch[];
  highestMatchPercentage: number;
  releaseDate?: string;
  homeUrl?: string;
  codeUrl?: string;
}

/**
 * Represents the general information about a detected snippet including
 * core-specific information such as licenses and issue counts. This data will
 * be used to display snippet overviews in a table.
 */
export type Snippet = Omit<MinimalSnippet, 'matches'> & {
  matchCount: number;
  licenses: SnippetLicense[];
  issueCounts: SnippetIssueCounts;
  rejectionDetails?: SnippetRejectionDetails;
  labels?: PackageLabelAssignmentWithName[];
};

/**
 * Represents the minimal set of package-level information that be derived
 * from MinimalSnippets (prior to hydration).
 */
export type MinimalSnippetPackage = {
  id: string;
  name: string;
  versionCount: number;
  matchCount: number;
};

/**
 * Represents a package that contains one or more detected snippets.
 * Values of the underlying snippets are aggregated to provide an overview
 * of the package as a whole.
 */
export type SnippetPackage = MinimalSnippetPackage & {
  issueCounts: SnippetIssueCounts;
  labels: PackageLabelAssignmentWithName[];
  isFullyRejected: boolean;
};

/**
 * Represents other versions of a snippet detected in the user's project.
 */
export type OtherSnippetVersion = {
  version: string;
  matchCount: number;
};

/**
 * This type represents a minimal (in the core sense) version of a snippet's details that
 * we plan on getting from Sparkle. Notably, it lacks certain core-specific information
 * such as licenses and issue counts. We will have to hydrate that information post-fetch.
 */
export type MinimalSnippetDetails = MinimalSnippet & {
  otherVersions: OtherSnippetVersion[];
};

/**
 * Represents detailed information about a detected snippet including
 * other versions, licenses, issue counts, and rejection details. This data will
 * be used to display detailed snippet information in a drawer or detail view.
 */
export type SnippetDetails = MinimalSnippetDetails & {
  matchCount: number;
  licenses: SnippetLicense[];
  issueCounts: SnippetIssueCounts;
  rejectionDetails?: SnippetRejectionDetails;
  labels?: PackageLabelAssignmentWithName[];
};

export const rejectionStatusFilterOptions = ['rejected', 'unrejected'] as const;
export type RejectionStatusFilterOption = typeof rejectionStatusFilterOptions[number];

/**
 * Represents the filter params available for snippets.
 */
export type SnippetsFilterParams = {
  path: string;
  ids?: string[];
  packageIds?: string[];
  search?: string;
  rejectionStatus?: RejectionStatusFilterOption[];
  packageLabels?: string[];
};

/**
 * Snippet paths can be filtered by everything in SnippetsFilterParams with 'path' being optional.
 * When no path is provided, we fetch and aggregate all snippets up to the root. When path _is_
 * provided, we consider only the snippets within that path.
 */
export type GetSnippetPathsParams = Partial<SnippetsFilterParams>;

/**
 * Represents the path to which a code snippet was detected.
 * In general, there is a "many-to-many" relationship between snippets and paths.
 */
export type SnippetPath = {
  type: 'directory' | 'file';
  name: string;
  path: string;
  count: number;
};

export type SnippetPathsResponse = {
  paths: SnippetPath[];
};

/**
 * Possible sorting directions.
 */
const sortDirections = ['asc', 'desc'] as const;

/**
 * Possible sorting fields for fetching snippets.
 */
const snippetsSortFields = [
  'package',
  'matchCount',
] as const;

/**
 * Represents the sorting options (as values) available for snippets.
 * These are used for input validation (since types don't exist at runtime).
 */
export const snippetsSortOptions = snippetsSortFields.flatMap(
  (field) => sortDirections.map((direction) => `${field}_${direction}` as const),
);

/**
 * Represents the sorting options (as a type) available for snippets.
 */
export type SnippetsSort = typeof snippetsSortOptions[number];

/**
 * The default sorting mode for snippets (if not specified).
 */
export const defaultSnippetsSort: SnippetsSort = 'matchCount_desc';

/**
 * Represents the sorting params available for snippets.
 */
export type SnippetsSortParams = {
  sort?: SnippetsSort;
};

/**
 * Represents the parameters that can be used when fetching snippets.
 */
export type GetSnippetsParams = SnippetsFilterParams & SnippetsSortParams & PaginatedRequestQuery & {
  includeUnresolvedSnippets?: boolean;
};

/**
 * Response type for the "GET snippets" endpoint.
 */
export type SnippetsResponse = PaginatedResponseWithTotal<Snippet>;

/**
 * Represents the parameters that can be used when fetching snippet packages.
 */
export type GetSnippetPackagesParams = SnippetsFilterParams & SnippetsSortParams & PaginatedRequestQuery;

/**
 * Response type for the "GET snippet packages" endpoint.
 */
export type SnippetPackagesResponse = PaginatedResponseWithTotal<SnippetPackage>;

export type SnippetDetailsResponse = {
  snippet: SnippetDetails;
};

export type SnippetCodeLine = {
  line: string;
  lineNumber: number;
  isHighlighted: boolean;
};

/**
 * Represents a match between reference code and a detected code snippet.
 * In general, one snippet can have multiple matches. This indicates that
 * the same snippet was found in multiple places within the user’s project.
 */
export type SnippetMatchDetails = SnippetMatch & {
  referenceCode: SnippetCodeLine[];
  detectedCode: SnippetCodeLine[];
};

export type SnippetMatchDetailsResponse = {
  matchDetails: SnippetMatchDetails;
};

/**
 * Controls how snippets are loaded and displayed in the UI.
 */
export type SnippetGrouping = 'ungrouped' | 'package';

export const defaultSnippetGrouping: SnippetGrouping = 'package';

/**
 * Represents the query params used on the frontend.
 */
export type SnippetsQueryParams = GetSnippetsParams & {
  /**
   * Determines if the drawer is open, and if so, the ID of the snippet.
   */
  selectedSnippetId?: string;
  /**
   * The older revision locator (used when comparing snippets).
   */
  olderRevisionLocator?: string;
  /**
   * Whether to group snippets by package.
   */
  grouping?: SnippetGrouping;
};
